import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import type { District, Godown, Report } from "../lib/api";
import { verifyUrl, verifyViewUrl } from "../lib/api";
import {
  DASH,
  fmtInt,
  isPass,
  useSHDistricts,
  useSHGodowns,
  useSHReports,
  useSHState,
} from "./data";

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/** "YYYY-MM" bucket key for a report's filing date. */
function monthKey(iso: string): string {
  return (iso || "").slice(0, 7);
}
/** "01 Aug 2026" long date. */
function longDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return DASH;
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}
/** "10:04" 24h time. */
function timeOf(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });
}
export function grainLabel(g?: Godown | null): string | null {
  if (!g) return null;
  const set = new Set<string>();
  (g.grain_configs ?? []).forEach((c) => set.add(c.grain_type));
  if (!set.size && g.grain_type) set.add(g.grain_type);
  if (!set.size) return null;
  if (set.size === 1) return [...set][0].toUpperCase();
  return `${set.size} GRAINS`;
}

export function Reports() {
  const [params, setParams] = useSearchParams();

  const stateQ = useSHState();
  const districtsQ = useSHDistricts();
  const godownsQ = useSHGodowns();
  const reportsQ = useSHReports();

  const state = stateQ.data?.state;
  const districts = useMemo(
    () =>
      [...(districtsQ.data?.districts ?? [])].sort((a, b) =>
        a.name.localeCompare(b.name),
      ),
    [districtsQ.data],
  );
  const godowns = godownsQ.data?.godowns ?? [];
  const reports = reportsQ.data?.reports ?? [];

  // Drill-down + selection. `godown` in the URL is the source of truth
  // for the report view, so District-detail deep links land here.
  const selectedGodownId = params.get("godown")
    ? Number(params.get("godown"))
    : null;
  const [openDistId, setOpenDistId] = useState<number | null>(null);
  const [openPlace, setOpenPlace] = useState<string | null>(null);

  const selectGodown = (id: number | null) => {
    const next = new URLSearchParams(params);
    if (id == null) next.delete("godown");
    else next.set("godown", String(id));
    setParams(next, { replace: false });
  };

  const godownsOf = (districtId: number) =>
    godowns.filter((g) => g.district_id === districtId);
  const placesOf = (districtId: number) => {
    const set = new Set<string>();
    godownsOf(districtId).forEach((g) => set.add((g.place ?? "").trim()));
    return [...set].sort((a, b) =>
      a === "" ? 1 : b === "" ? -1 : a.localeCompare(b),
    );
  };
  const reportsOf = (godownId: number) =>
    reports.filter((r) => r.godown_id === godownId);

  const selectedGodown =
    selectedGodownId != null
      ? godowns.find((g) => g.id === selectedGodownId) ?? null
      : null;

  const crumb = (
    <p className="crumb">{(state?.name ?? "STATE").toUpperCase()} / REPORTS</p>
  );

  // ---- report view (a godown is selected) ---------------------------
  if (selectedGodownId != null) {
    const godown = selectedGodown;
    const district =
      godown && godown.district_id != null
        ? districts.find((d) => d.id === godown.district_id) ?? null
        : null;
    return (
      <>
        {crumb}
        <GodownReport
          godown={godown}
          district={district}
          reports={godown ? reportsOf(godown.id) : []}
          onBack={() => {
            if (godown && godown.district_id != null) {
              setOpenDistId(godown.district_id);
              setOpenPlace((godown.place ?? "").trim());
            }
            selectGodown(null);
          }}
        />
      </>
    );
  }

  // ---- finder -------------------------------------------------------
  const openDist =
    openDistId != null ? districts.find((d) => d.id === openDistId) ?? null : null;

  let crumbTrail: JSX.Element;
  let count = "";
  let cards: JSX.Element;

  if (openDist && openPlace != null) {
    const gs = godownsOf(openDist.id).filter(
      (g) => (g.place ?? "").trim() === openPlace,
    );
    crumbTrail = (
      <div className="find-crumb">
        <button onClick={() => { setOpenDistId(null); setOpenPlace(null); }}>
          ALL DISTRICTS
        </button>{" "}
        /{" "}
        <button onClick={() => setOpenPlace(null)}>
          {openDist.name.toUpperCase()}
        </button>{" "}
        / {(openPlace || "PLACE NOT SET").toUpperCase()}
      </div>
    );
    count = `${gs.length} GODOWN${gs.length === 1 ? "" : "S"}`;
    cards = (
      <div className="grid-cards">
        {gs.map((g) => (
          <GodownCard
            key={g.id}
            godown={g}
            reports={reportsOf(g.id)}
            onOpen={() => selectGodown(g.id)}
          />
        ))}
      </div>
    );
  } else if (openDist) {
    const places = placesOf(openDist.id);
    const gCount = godownsOf(openDist.id).length;
    crumbTrail = (
      <div className="find-crumb">
        <button onClick={() => { setOpenDistId(null); setOpenPlace(null); }}>
          ALL DISTRICTS
        </button>{" "}
        / {openDist.name.toUpperCase()}
      </div>
    );
    count = gCount
      ? `${places.filter(Boolean).length} PLACES · ${gCount} GODOWNS`
      : "NOT SET UP";
    cards = gCount ? (
      <div className="grid-cards">
        {places.map((pl) => (
          <PlaceCard
            key={pl || "__none"}
            place={pl}
            district={openDist}
            godowns={godownsOf(openDist.id).filter(
              (g) => (g.place ?? "").trim() === pl,
            )}
            reports={reports}
            onOpen={() => setOpenPlace(pl)}
          />
        ))}
      </div>
    ) : (
      <p className="no-match">
        {openDist.name} has no godowns yet. Its district head adds them once
        they've confirmed their profile.
      </p>
    );
  } else {
    crumbTrail = <div className="find-crumb">ALL DISTRICTS</div>;
    count = `${districts.length} DISTRICT${districts.length === 1 ? "" : "S"}`;
    cards = (
      <div className="grid-cards">
        {districts.map((d) => (
          <DistrictCard
            key={d.id}
            district={d}
            godowns={godownsOf(d.id)}
            placeCount={placesOf(d.id).filter(Boolean).length}
            onOpen={() => { setOpenDistId(d.id); setOpenPlace(null); }}
          />
        ))}
      </div>
    );
  }

  return (
    <>
      {crumb}
      <div className="page-head">
        <div>
          <h1>Reports</h1>
          <p>
            Find a district, place or godown to see every grading report filed
            there.
          </p>
        </div>
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div className="find-bar">
          <Search
            districts={districts}
            godowns={godowns}
            onPickDistrict={(id) => { setOpenDistId(id); setOpenPlace(null); }}
            onPickPlace={(id, place) => { setOpenDistId(id); setOpenPlace(place); }}
            onPickGodown={(id) => selectGodown(id)}
          />
          <span className="find-count">{count}</span>
        </div>
        {crumbTrail}
        {cards}
      </div>
    </>
  );
}

// ------------------------------------------------------------------
// Finder cards
// ------------------------------------------------------------------

function DistrictCard({
  district,
  godowns,
  placeCount,
  onOpen,
}: {
  district: District;
  godowns: Godown[];
  placeCount: number;
  onOpen: () => void;
}) {
  const empty = godowns.length === 0;
  return (
    <button className="gcard" onClick={onOpen}>
      <b>{district.name}</b>
      <em>
        {district.code || `#${district.id}`}
        {district.zone ? ` · ${district.zone.toUpperCase()} ZONE` : ""}
      </em>
      <span className={"figs" + (empty ? " warn" : "")}>
        {empty
          ? "No godowns yet"
          : `${placeCount} place${placeCount === 1 ? "" : "s"} · ${
              godowns.length
            } godown${godowns.length === 1 ? "" : "s"}`}
      </span>
      {empty ? null : <span className="go">Open places →</span>}
    </button>
  );
}

function PlaceCard({
  place,
  district,
  godowns,
  reports,
  onOpen,
}: {
  place: string;
  district: District;
  godowns: Godown[];
  reports: Report[];
  onOpen: () => void;
}) {
  const gids = new Set(godowns.map((g) => g.id));
  const filed = reports.filter(
    (r) => r.godown_id != null && gids.has(r.godown_id),
  ).length;
  return (
    <button className="gcard" onClick={onOpen}>
      <b>{place || "Place not set"}</b>
      <em>{district.name.toUpperCase()} DISTRICT</em>
      <span className="figs">
        {godowns.length} godown{godowns.length === 1 ? "" : "s"} ·{" "}
        {fmtInt(filed)} report{filed === 1 ? "" : "s"}
      </span>
      <span className="go">Open godowns →</span>
    </button>
  );
}

function GodownCard({
  godown,
  reports,
  onOpen,
}: {
  godown: Godown;
  reports: Report[];
  onOpen: () => void;
}) {
  const filed = reports.length;
  const passed = reports.filter((r) => isPass(r.verdict)).length;
  const pct = filed ? Math.round((passed / filed) * 100) : null;
  const grain = grainLabel(godown);
  return (
    <button className="gcard" onClick={onOpen}>
      <b>{godown.name}</b>
      <em>
        {(godown.place ?? "").trim() || "PLACE NOT SET"}
        {grain ? ` · ${grain}` : ""}
      </em>
      <span className={"figs" + (filed ? "" : " warn")}>
        {filed ? `${fmtInt(filed)} reports · ${pct}% passed` : "No reports yet"}
      </span>
      <span className="go">Open reports →</span>
    </button>
  );
}

// ------------------------------------------------------------------
// Search combobox
// ------------------------------------------------------------------

type Hit =
  | { kind: "d"; label: string; meta: string; districtId: number }
  | { kind: "p"; label: string; meta: string; districtId: number; place: string }
  | { kind: "w"; label: string; meta: string; godownId: number };

function Search({
  districts,
  godowns,
  onPickDistrict,
  onPickPlace,
  onPickGodown,
}: {
  districts: District[];
  godowns: Godown[];
  onPickDistrict: (districtId: number) => void;
  onPickPlace: (districtId: number, place: string) => void;
  onPickGodown: (godownId: number) => void;
}) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(0);
  const blurTimer = useRef<number | null>(null);

  const distById = useMemo(
    () => new Map(districts.map((d) => [d.id, d])),
    [districts],
  );

  const hits: Hit[] = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return [];
    const out: Hit[] = [];
    for (const d of districts) {
      if (
        d.name.toLowerCase().includes(needle) ||
        (d.code ?? "").toLowerCase().includes(needle) ||
        (d.zone ?? "").toLowerCase().includes(needle)
      ) {
        const gs = godowns.filter((g) => g.district_id === d.id);
        out.push({
          kind: "d",
          label: `${d.name} district`,
          meta: `${d.code || `#${d.id}`} · ${gs.length} GODOWNS`,
          districtId: d.id,
        });
      }
    }
    const seenPlace = new Set<string>();
    for (const g of godowns) {
      const place = (g.place ?? "").trim();
      if (!place || g.district_id == null) continue;
      const key = `${g.district_id}::${place.toLowerCase()}`;
      if (seenPlace.has(key)) continue;
      if (place.toLowerCase().includes(needle)) {
        seenPlace.add(key);
        const d = distById.get(g.district_id);
        out.push({
          kind: "p",
          label: place,
          meta: `${(d?.name ?? "").toUpperCase()} DISTRICT`,
          districtId: g.district_id,
          place,
        });
      }
    }
    for (const g of godowns) {
      if (
        g.name.toLowerCase().includes(needle) ||
        (g.place ?? "").toLowerCase().includes(needle)
      ) {
        const d = g.district_id != null ? distById.get(g.district_id) : null;
        out.push({
          kind: "w",
          label: g.name,
          meta: `${(g.place ?? "").trim().toUpperCase() || "PLACE NOT SET"}${
            d ? ` · ${d.name.toUpperCase()}` : ""
          }`,
          godownId: g.id,
        });
      }
    }
    return out.slice(0, 12);
  }, [q, districts, godowns, distById]);

  const pick = (i: number) => {
    const h = hits[i];
    if (!h) return;
    setQ("");
    setOpen(false);
    if (h.kind === "d") onPickDistrict(h.districtId);
    else if (h.kind === "p") onPickPlace(h.districtId, h.place);
    else onPickGodown(h.godownId);
  };

  const GROUP: Record<Hit["kind"], string> = {
    d: "DISTRICTS",
    p: "PLACES",
    w: "GODOWNS",
  };
  const IC: Record<Hit["kind"], string> = { d: "DT", p: "PL", w: "GD" };

  let lastKind = "";
  return (
    <div className="combo">
      <input
        className="search big"
        type="text"
        role="combobox"
        autoComplete="off"
        aria-expanded={open}
        aria-autocomplete="list"
        placeholder="Search a district, place or godown"
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setSel(0);
          setOpen(!!e.target.value.trim());
        }}
        onFocus={() => setOpen(!!q.trim())}
        onBlur={() => {
          blurTimer.current = window.setTimeout(() => setOpen(false), 120);
        }}
        onKeyDown={(e) => {
          if (!open) return;
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setSel((s) => Math.min(s + 1, hits.length - 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setSel((s) => Math.max(s - 1, 0));
          } else if (e.key === "Enter") {
            e.preventDefault();
            pick(sel);
          } else if (e.key === "Escape") {
            setOpen(false);
          }
        }}
      />
      {open && (
        <div className="combo-list" role="listbox">
          {hits.length === 0 ? (
            <p className="combo-none">
              Nothing matches. Try a district, a place, or a godown name.
            </p>
          ) : (
            hits.map((h, i) => {
              const header =
                h.kind !== lastKind ? (
                  <p className="combo-group" key={`g-${h.kind}`}>
                    {GROUP[h.kind]}
                  </p>
                ) : null;
              lastKind = h.kind;
              return (
                <span key={`${h.kind}-${i}`}>
                  {header}
                  <button
                    className={"combo-item" + (i === sel ? " sel" : "")}
                    role="option"
                    aria-selected={i === sel}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      if (blurTimer.current) window.clearTimeout(blurTimer.current);
                      pick(i);
                    }}
                  >
                    <span className={"ic " + h.kind}>{IC[h.kind]}</span>
                    <span>
                      <b>{h.label}</b>
                      <span>{h.meta}</span>
                    </span>
                  </button>
                </span>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------
// Godown grading report (grading-only, real data)
// ------------------------------------------------------------------

type Mode = "all" | "month" | "date";

export function GodownReport({
  godown,
  district,
  reports,
  onBack,
  backLabel = "Back to godowns",
}: {
  godown: Godown | null;
  district: District | null;
  reports: Report[];
  onBack: () => void;
  backLabel?: string;
}) {
  const [mode, setMode] = useState<Mode>("all");
  const [viewing, setViewing] = useState<Report | null>(null);
  const sorted = useMemo(
    () =>
      [...reports].sort((a, b) =>
        (b.created_at || "").localeCompare(a.created_at || ""),
      ),
    [reports],
  );
  const months = useMemo(() => {
    const set = new Set<string>();
    sorted.forEach((r) => set.add(monthKey(r.created_at)));
    return [...set].filter(Boolean).sort().reverse();
  }, [sorted]);

  const [monthVal, setMonthVal] = useState<string>("");
  const [dateVal, setDateVal] = useState<string>("");
  useEffect(() => {
    if (!monthVal && months.length) setMonthVal(months[0]);
    if (!dateVal && sorted.length) setDateVal((sorted[0].created_at || "").slice(0, 10));
  }, [months, sorted, monthVal, dateVal]);

  const rows =
    mode === "all"
      ? sorted
      : mode === "month"
        ? sorted.filter((r) => monthKey(r.created_at) === monthVal)
        : sorted.filter((r) => (r.created_at || "").slice(0, 10) === dateVal);

  const passed = rows.filter((r) => isPass(r.verdict)).length;
  const failed = rows.length - passed;
  const pct = rows.length ? Math.round((passed / rows.length) * 100) : null;
  const agents = new Set(
    reports.map((r) => (r.user_name || "").trim()).filter(Boolean),
  );
  const grain = grainLabel(godown);

  const period =
    mode === "all"
      ? "till date"
      : mode === "month" && monthVal
        ? `${MONTHS[+monthVal.slice(5) - 1]} ${monthVal.slice(0, 4)}`
        : dateVal
          ? longDate(dateVal)
          : "selected date";

  return (
    <>
      <button className="btn back-link" onClick={onBack}>
        <svg viewBox="0 0 16 16" fill="none">
          <path
            d="M10 3.5L5.5 8l4.5 4.5"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span>{backLabel}</span>
      </button>
      <div className="card" style={{ padding: 0 }}>
        <div className="centre-report">
          <div className="cr-head">
            <div>
              <h3>{godown ? godown.name : "Godown"}</h3>
              <p className="sub">
                {(godown?.place ?? "").trim() || "Place not set"}
                {district ? ` · ${district.name} district` : ""}
                {grain ? ` · ${grain.toLowerCase()}` : ""}
              </p>
              <div className="attrib">
                <span className="attrib-av">
                  {(godown?.name ?? "G").slice(0, 2).toUpperCase()}
                </span>
                <span className="attrib-text">
                  Filed by field devices under this godown
                  <em>
                    {fmtInt(reports.length)} report
                    {reports.length === 1 ? "" : "s"}
                    {agents.size
                      ? ` · ${agents.size} QC agent${agents.size === 1 ? "" : "s"}`
                      : ""}
                  </em>
                </span>
              </div>
            </div>
          </div>

          <div className="period">
            <div className="seg">
              <button
                className={mode === "date" ? "on" : ""}
                onClick={() => setMode("date")}
              >
                By date
              </button>
              <button
                className={mode === "month" ? "on" : ""}
                onClick={() => setMode("month")}
                disabled={!months.length}
              >
                By month
              </button>
              <button
                className={mode === "all" ? "on" : ""}
                onClick={() => setMode("all")}
              >
                Till date
              </button>
            </div>
            {mode === "month" && months.length > 0 && (
              <select
                value={monthVal}
                onChange={(e) => setMonthVal(e.target.value)}
              >
                {months.map((m) => (
                  <option key={m} value={m}>
                    {MONTHS[+m.slice(5) - 1]} {m.slice(0, 4)}
                  </option>
                ))}
              </select>
            )}
            {mode === "date" && (
              <input
                type="date"
                value={dateVal}
                onChange={(e) => setDateVal(e.target.value)}
              />
            )}
            <span className="period-count">
              {rows.length} report{rows.length === 1 ? "" : "s"}
            </span>
          </div>

          {rows.length === 0 ? (
            <div className="empty" style={{ margin: "4px 20px 22px" }}>
              <b>No reports for this period</b>
              <p>
                Nothing was graded at {godown?.name ?? "this godown"} in the
                selected period. Try another date or switch to Till date.
              </p>
            </div>
          ) : (
            <>
              <div className="period-sum">
                <div>
                  <span>REPORTS</span>
                  <b>{fmtInt(rows.length)}</b>
                </div>
                <div>
                  <span>PASSED</span>
                  <b>{fmtInt(passed)}</b>
                </div>
                <div>
                  <span>PASS RATE</span>
                  <b>
                    {pct}
                    <small>%</small>
                  </b>
                </div>
                <div>
                  <span>FAILED</span>
                  <b>{fmtInt(failed)}</b>
                </div>
              </div>
              <div className="tscroll">
                <table className="stock">
                  <thead>
                    <tr>
                      <th>REPORT ID</th>
                      <th>GRADED ON</th>
                      <th>GRAIN</th>
                      <th>VERDICT</th>
                      <th className="r">MOISTURE</th>
                      <th>QC AGENT</th>
                      <th className="r" />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const ok = isPass(r.verdict);
                      return (
                        <tr key={r.id}>
                          <td>
                            <b className="mono" style={{ fontSize: 12.5 }}>
                              {r.report_id}
                            </b>
                          </td>
                          <td>
                            {longDate(r.created_at)}
                            <span className="hash">
                              {timeOf(r.created_at)} IST
                            </span>
                          </td>
                          <td>
                            <span className="mono">
                              {(r.grain_type || "maize").toUpperCase()}
                            </span>
                          </td>
                          <td>
                            <span
                              className={"tag " + (ok ? "tag-live" : "tag-setup")}
                            >
                              <span className="dot" />
                              {ok ? "Pass" : "Fail"}
                            </span>
                          </td>
                          <td className="r mono">
                            {r.moisture != null ? `${r.moisture}%` : DASH}
                          </td>
                          <td>{r.user_name || DASH}</td>
                          <td className="r nowrap">
                            <button
                              className="link"
                              onClick={() => setViewing(r)}
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={3}>TOTAL · {period.toUpperCase()}</td>
                      <td>{fmtInt(passed)} passed</td>
                      <td className="r">{pct != null ? `${pct}%` : DASH}</td>
                      <td colSpan={2} />
                    </tr>
                  </tfoot>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
      {viewing && (
        <ReportModal report={viewing} onClose={() => setViewing(null)} />
      )}
    </>
  );
}

/** A browser-based PDF viewer for one grading report, opened from the
 * report table. Embeds the stored PDF (served inline via /api/verify)
 * in an iframe so it's read in place using the browser's own PDF
 * viewer, with explicit Download / Open-in-new-tab actions in the
 * footer. */
function ReportModal({
  report,
  onClose,
}: {
  report: Report;
  onClose: () => void;
}) {
  const ok = isPass(report.verdict);
  const viewSrc = verifyViewUrl(report.report_id);
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(11,48,73,.45)",
        display: "grid",
        placeItems: "center",
        zIndex: 60,
        padding: 20,
      }}
    >
      <div
        className="card"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        style={{
          width: "min(1000px, 100%)",
          height: "88vh",
          padding: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div className="card-head" style={{ alignItems: "center", gap: 12 }}>
          <div>
            <h2 className="mono" style={{ letterSpacing: 0 }}>
              {report.report_id}
            </h2>
            <p>Grading report</p>
          </div>
          <div
            className="right"
            style={{ display: "flex", gap: 8, alignItems: "center" }}
          >
            <span className={"tag " + (ok ? "tag-live" : "tag-setup")}>
              <span className="dot" />
              {ok ? "Pass" : "Fail"}
            </span>
            <button className="x" onClick={onClose} aria-label="Close">
              ×
            </button>
          </div>
        </div>
        {/* Browser's built-in PDF viewer (view + its own download/print
            controls). The stored PDF is served inline for this. */}
        <div style={{ flex: 1, minHeight: 0, background: "#EEF1EF" }}>
          <iframe
            title={`Report ${report.report_id}`}
            src={viewSrc}
            style={{ width: "100%", height: "100%", border: 0, display: "block" }}
          />
        </div>
        <div
          className="drawer-foot"
          style={{ position: "static", borderTop: "1px solid var(--line)" }}
        >
          <a className="btn" href={verifyUrl(report.report_id)}>
            Download PDF
          </a>
          <a className="btn" href={viewSrc} target="_blank" rel="noreferrer">
            Open in new tab
          </a>
          <button className="btn btn-gold" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
